import type { Activity } from "~/v2/api/rest/account";
import {
  ACTIVITIES_UNAVAILABLE_VALUE,
  formatActivityAmount,
  formatActivityDateOnly,
  formatActivityDescription,
} from "~/v3-poc/features/workspace/widgets/activities/activities.utils";
import type { OverviewActivityFilter, OverviewActivityRow } from "../types";

export const OVERVIEW_ACTIVITY_ROW_LIMIT = 8;

/**
 * Overview date column: same timezone contract as the Activities widget
 * (broker `date` in UTC, else NY day of `transaction_time`), date-only.
 */
export const formatOverviewActivityDate = formatActivityDateOnly;

export const OVERVIEW_ACTIVITY_FILTER_OPTIONS: ReadonlyArray<{
  value: OverviewActivityFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "funding", label: "Funding" },
  { value: "trades", label: "Trades" },
  { value: "fees", label: "Fees" },
  { value: "corporate", label: "Corporate Actions" },
];

/**
 * Maps broker `activity_type` codes to the Overview's coarse buckets. Codes not
 * listed here only appear under the "All" filter. Interest sits with fees so the
 * five-bucket control stays aligned with the mock.
 */
const CATEGORY_FOR_TYPE: Record<
  string,
  Exclude<OverviewActivityFilter, "all">
> = {
  // Funding — deposits, withdrawals, transfers, journals.
  CSD: "funding",
  CSW: "funding",
  CSR: "funding",
  TRANS: "funding",
  JNLC: "funding",
  JNLS: "funding",
  // Trades — equity and option fills.
  FILL: "trades",
  OPTRD: "trades",
  // Fees & interest.
  FEE: "fees",
  DIVFEE: "fees",
  CFEE: "fees",
  INT: "fees",
  // Corporate actions — dividends, option lifecycle, splits/mergers.
  DIV: "corporate",
  DIVCGL: "corporate",
  DIVCGS: "corporate",
  DIVNRA: "corporate",
  DIVROC: "corporate",
  OPEXC: "corporate",
  OPEXP: "corporate",
  OPASN: "corporate",
  SPLIT: "corporate",
  MERGER: "corporate",
  SPIN: "corporate",
  NAME: "corporate",
};

export const categoryForActivity = (
  activity: Activity,
): Exclude<OverviewActivityFilter, "all"> | undefined => {
  const type =
    typeof activity.activity_type === "string"
      ? activity.activity_type
      : undefined;
  return type ? CATEGORY_FOR_TYPE[type] : undefined;
};

export const matchesOverviewFilter = (
  activity: Activity,
  filter: OverviewActivityFilter,
): boolean => filter === "all" || categoryForActivity(activity) === filter;

const finiteNumber = (value: unknown): number | undefined => {
  if (
    (typeof value !== "string" && typeof value !== "number") ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Signed numeric amount used only for direction colouring. */
const activityAmount = (activity: Activity): number | undefined => {
  const net = finiteNumber((activity as { net_amount?: unknown }).net_amount);
  if (net !== undefined) {
    return net;
  }
  const price = finiteNumber((activity as { price?: unknown }).price);
  const qty = finiteNumber((activity as { qty?: unknown }).qty);
  if (price !== undefined && qty !== undefined) {
    return price * qty;
  }
  return undefined;
};

export const toOverviewActivityRow = (
  activity: Activity,
  index: number,
): OverviewActivityRow => ({
  id:
    typeof activity.id === "string" && activity.id
      ? activity.id
      : `activity-${index}`,
  description: formatActivityDescription(activity),
  typeLabel:
    typeof activity.activity_type === "string" && activity.activity_type
      ? activity.activity_type
      : ACTIVITIES_UNAVAILABLE_VALUE,
  dateLabel: formatOverviewActivityDate(activity),
  amountLabel: formatActivityAmount(activity),
  amount: activityAmount(activity),
});

/** ISO timestamp for `now - days`, used to scope the "last N days" request. */
export const isoDaysAgo = (days: number, now: number = Date.now()): string =>
  new Date(now - days * 24 * 60 * 60 * 1000).toISOString();
